import { useCallback, useEffect, useRef, useState } from "react";
import { Routes, Route, NavLink, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Combobox } from "@/components/common/Combobox";
import { downloadCSV } from "@/lib/csv";
import { toast } from "sonner";
import {
  DataTable,
  PageTitle,
  MasterCrudPage,
  useRouteModulePermissions,
} from "@/components/common/Crud";
import { MessageCircle, Printer } from "lucide-react";

function FeesNav({ items }) {
  return (
    <div className="border-b border-slate-200 mb-3 flex flex-wrap gap-1 text-xs">
      {items.map((i) => (
        <NavLink
          key={i.to}
          to={i.to}
          data-testid={`fees-nav-${i.to.split("/").pop()}`}
          className={({ isActive }) =>
            `px-3 py-1.5 border-b-2 -mb-px ${isActive ? "border-blue-600 text-blue-600 font-medium" : "border-transparent text-slate-600 hover:text-slate-900"}`
          }
        >
          {i.label}
        </NavLink>
      ))}
    </div>
  );
}

const CASH_PAYMENT_TYPES = [{ value: "cash", label: "Cash" }];
const BANK_PAYMENT_TYPES = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "upi", label: "UPI" },
  { value: "neft", label: "NEFT" },
  { value: "rtgs", label: "RTGS" },
  { value: "imps", label: "IMPS" },
  { value: "cheque", label: "Cheque" },
];

function inferLedgerMode(ledger) {
  if (!ledger) return null;
  const type = String(ledger.type || "").toLowerCase();
  const name = String(ledger.name || "").toLowerCase();
  const group = String(ledger.group_name || "").toLowerCase();
  if (
    type.includes("cash") ||
    group.includes("cash") ||
    name.includes("cash")
  ) {
    return "cash";
  }
  if (
    type.includes("bank") ||
    group.includes("bank") ||
    name.includes("bank") ||
    name.includes("current") ||
    name.includes("saving")
  ) {
    return "bank";
  }
  return null;
}

function feeCollectionLedgers(ledgers) {
  return (ledgers || []).filter((ledger) => inferLedgerMode(ledger));
}

function paymentTypeOptions(modeKind) {
  return modeKind === "bank" ? BANK_PAYMENT_TYPES : CASH_PAYMENT_TYPES;
}

function studentComboboxOptions(students) {
  return (students || []).map((student) => ({
    value: String(student.id),
    label:
      `${student.admission_no || ""} — ${student.first_name || ""} ${student.last_name || ""}`.trim(),
    sub: [
      student.class_name || student.class || "",
      student.section || "",
      student.mobile || student.father_mobile || student.guardian_mobile || "",
    ]
      .filter(Boolean)
      .join(" • "),
  }));
}

function distributeAmountAcrossDues(lines, amount) {
  let remaining = Math.max(0, Number(amount) || 0);
  return (lines || []).map((line) => {
    const due = Math.max(0, Number(line.due) || 0);
    const nextAmount = Math.min(remaining, due);
    remaining -= nextAmount;
    return {
      ...line,
      amount: nextAmount,
    };
  });
}

function formatDisplayDate(value) {
  if (!value) return "-";
  const parts = String(value).split("-");
  if (parts.length !== 3) return String(value);
  const [year, month, day] = parts;
  if (!year || !month || !day) return String(value);
  return `${day}-${month}-${year}`;
}

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function sortByDueDate(rows) {
  return [...(rows || [])].sort((left, right) => {
    const leftDate = String(left?.due_date || "9999-12-31");
    const rightDate = String(right?.due_date || "9999-12-31");
    if (leftDate === rightDate) {
      return Number(left?.id || 0) - Number(right?.id || 0);
    }
    return leftDate.localeCompare(rightDate);
  });
}

function openAuthedReport(path) {
  // Keep helper name stable for existing callers; open via header-auth fetch.
  downloadAuthedReport(path, "report.pdf", { openInNewTab: true });
}

async function fetchAuthedBlob(path) {
  const normalizedPath = String(path || "").replace(/^\/api(?=\/)/i, "");
  const response = await api.get(normalizedPath, { responseType: "blob" });
  const blob = response.data;
  const disposition = response.headers?.["content-disposition"] || "";
  const fileNameMatch = disposition.match(/filename="?([^";]+)"?/i);
  return {
    blob,
    fileName: fileNameMatch?.[1] || null,
  };
}

function triggerBlobDownload(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName || "report.pdf";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

async function downloadAuthedReport(path, fallbackFileName, options = {}) {
  if (options.openInNewTab) {
    const { blob } = await fetchAuthedBlob(path);
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return;
  }

  const { blob, fileName } = await fetchAuthedBlob(path);
  const finalName = fileName || fallbackFileName;
  triggerBlobDownload(blob, finalName);
}

async function shareAuthedPdfOnWhatsApp(path, fallbackFileName, title) {
  const { blob, fileName } = await fetchAuthedBlob(path);
  const finalFileName = fileName || fallbackFileName;
  const pdfFile = new File([blob], finalFileName, {
    type: blob.type || "application/pdf",
  });

  if (
    typeof navigator !== "undefined" &&
    navigator.canShare &&
    navigator.share &&
    navigator.canShare({ files: [pdfFile] })
  ) {
    await navigator.share({
      title,
      text: title,
      files: [pdfFile],
    });
    return;
  }

  triggerBlobDownload(blob, finalFileName);
  window.open(
    `https://wa.me/?text=${encodeURIComponent(`${title} downloaded. Attach the generated PDF in WhatsApp if direct file share is not supported on this device.`)}`,
    "_blank",
  );
}

function ReceiptActionDialog({
  open,
  onOpenChange,
  title,
  description,
  actionLabel,
  actionTone = "default",
  onConfirm,
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md border-slate-200">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-xl text-slate-900">
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm leading-6 text-slate-600">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className={
              actionTone === "danger"
                ? "bg-rose-600 hover:bg-rose-700 text-white"
                : actionTone === "success"
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : "bg-sky-600 hover:bg-sky-700 text-white"
            }
          >
            {actionLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function nextChequeStatus(status) {
  if (status === "pending") return "deposited";
  if (status === "deposited") return "cleared";
  return null;
}

function CollectFee() {
  const permissions = useRouteModulePermissions("fees", "receipt");
  const { branchId, sessionId } = useAuth();
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(sessionId || "");
  const [selectedClassId, setSelectedClassId] = useState("");
  const [studentId, setStudentId] = useState("");
  const [assigns, setAssigns] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [modeLedger, setModeLedger] = useState("");
  const [instrumentType, setInstrumentType] = useState("cash");
  const [referenceNo, setReferenceNo] = useState("");
  const [bankName, setBankName] = useState("");
  const [chequeNo, setChequeNo] = useState("");
  const [chequeDate, setChequeDate] = useState("");
  const [utrNo, setUtrNo] = useState("");
  const [receiptDate, setReceiptDate] = useState(todayIsoDate());
  const [collectionAmount, setCollectionAmount] = useState("");
  const [lines, setLines] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(null);
  const [classesLoading, setClassesLoading] = useState(true);
  const [studentsLoading, setStudentsLoading] = useState(true);

  useEffect(() => {
    setSelectedSessionId(sessionId || "");
  }, [sessionId]);

  useEffect(() => {
    setClassesLoading(true);
    (async () => {
      try {
        const [classRes, ledgerRes] = await Promise.all([
          api.get("/classes", { params: { branch_id: branchId, limit: 500 } }),
          api.get("/ledgers", { params: { branch_id: branchId, limit: 1000 } }),
        ]);
        setClasses(classRes.data.items || []);
        setLedgers(ledgerRes.data.items || []);
      } finally {
        setClassesLoading(false);
      }
    })();
  }, [branchId]);

  const loadStudents = useCallback(async () => {
    if (!branchId || !selectedSessionId) {
      setStudents([]);
      return;
    }
    setStudentsLoading(true);
    try {
      const { data } = await api.get("/students", {
        params: {
          branch_id: branchId,
          session_id: selectedSessionId,
          class_id: selectedClassId || undefined,
          limit: 1000,
        },
      });
      setStudents(data.items || []);
    } finally {
      setStudentsLoading(false);
    }
  }, [branchId, selectedClassId, selectedSessionId]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  useEffect(() => {
    setStudentId("");
    setAssigns([]);
    setLines([]);
    setCollectionAmount("");
  }, [selectedClassId, selectedSessionId]);

  useEffect(() => {
    if (!studentId) {
      setAssigns([]);
      setLines([]);
      setCollectionAmount("");
      return;
    }
    (async () => {
      const { data } = await api.get("/fees/assign", {
        params: { student_id: studentId, session_id: selectedSessionId },
      });
      const pending = sortByDueDate(
        (data.items || []).filter((a) => (a.due ?? a.amount) > 0),
      );
      setAssigns(pending);
      setLines(
        pending.map((a) => ({
          fee_assign_id: a.id,
          fee_type_id: a.fee_type_id,
          ledger_name: ledgers.find((l) =>
            l.name
              ?.toLowerCase()
              ?.includes((a.fee_type_id || "")?.toLowerCase?.()),
          )?.name,
          amount: 0,
          due: a.due,
          narration: a.narration || "",
        })),
      );
    })();
  }, [studentId, selectedSessionId, ledgers]);

  const total = lines.reduce((s, l) => s + Number(l.amount || 0), 0);
  const stu = students.find((s) => String(s.id) === String(studentId));
  const studentOptions = studentComboboxOptions(students);
  const paymentLedgers = feeCollectionLedgers(ledgers);
  const selectedModeLedger = paymentLedgers.find(
    (l) => String(l.id) === String(modeLedger),
  );
  const modeKind = inferLedgerMode(selectedModeLedger);
  const paymentOptions = paymentTypeOptions(modeKind);

  useEffect(() => {
    const currentOptions = paymentTypeOptions(modeKind);
    const defaultType = currentOptions[0]?.value || "cash";
    if (!currentOptions.find((item) => item.value === instrumentType)) {
      setInstrumentType(defaultType);
    }
    if (modeKind === "cash") {
      setBankName("");
      setUtrNo("");
      setChequeNo("");
      setChequeDate("");
    }
  }, [modeKind, instrumentType]);

  const setAmount = (i, v) => {
    const cp = [...lines];
    cp[i].amount = Math.max(0, Math.min(Number(v) || 0, cp[i].due || 0));
    setLines(cp);
  };

  const setDistributedAmount = (value) => {
    setCollectionAmount(value);
    setLines((prev) => distributeAmountAcrossDues(prev, value));
  };

  const submit = async () => {
    if (!permissions.canAdd) {
      toast.error("You do not have permission to collect fee");
      return;
    }
    if (!studentId) return toast.error("Select student");
    const validLines = lines.filter((l) => Number(l.amount) > 0);
    if (validLines.length === 0)
      return toast.error("Enter amount for at least one fee head");
    if (!modeLedger) return toast.error("Select payment mode");
    if (modeKind !== "cash" && !selectedModeLedger) {
      return toast.error("Select cash or bank account");
    }
    if (
      ["neft", "rtgs", "imps", "upi", "bank_transfer"].includes(
        instrumentType,
      ) &&
      !utrNo.trim()
    ) {
      return toast.error("UTR/NEFT reference required");
    }
    if (instrumentType === "cheque" && !chequeNo.trim()) {
      return toast.error("Cheque details required");
    }

    const modeL = paymentLedgers.find(
      (l) => String(l.id) === String(modeLedger),
    );
    setBusy(true);
    try {
      const { data } = await api.post("/fees/receipt", {
        student_id: studentId,
        branch_id: branchId,
        session_id: selectedSessionId,
        date: receiptDate || todayIsoDate(),
        mode_ledger_id: modeLedger,
        mode_ledger_name: modeL?.name,
        instrument_type: instrumentType,
        reference_no: referenceNo || utrNo || chequeNo || "",
        bank_name: bankName || null,
        cheque_no: chequeNo || null,
        cheque_date: chequeDate || null,
        utr_no: utrNo || null,
        lines: validLines.map((l) => ({
          fee_assign_id: l.fee_assign_id,
          fee_type_id: l.fee_type_id,
          amount: Number(l.amount),
          narration: l.narration,
        })),
        remarks,
      });
      toast.success(`Receipt ${data.receipt_no} created`);
      setLastReceipt(data);
      setLines([]);
      setStudentId("");
      setRemarks("");
      setModeLedger("");
      setInstrumentType("cash");
      setReferenceNo("");
      setBankName("");
      setChequeNo("");
      setChequeDate("");
      setUtrNo("");
      setReceiptDate(todayIsoDate());
      setCollectionAmount("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Collection failed");
    }
    setBusy(false);
  };

  return (
    <div data-testid="collect-fee">
      <PageTitle
        title="Collect Fee"
        subtitle="Search a student, select fee heads, then collect against cash or bank account ledgers only."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 border border-slate-200 bg-white rounded-md p-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                Class
              </Label>
              <select
                className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white disabled:opacity-60 disabled:cursor-wait"
                value={selectedClassId}
                onChange={(e) => setSelectedClassId(e.target.value)}
                disabled={classesLoading}
                data-testid="select-class"
              >
                {classesLoading ? (
                  <option value="">Loading classes…</option>
                ) : (
                  classes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name || c.class_name || `Class ${c.id}`}
                    </option>
                  ))
                )}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                Student
              </Label>
              <Combobox
                value={String(studentId || "")}
                onChange={(value) => setStudentId(value)}
                options={studentOptions}
                placeholder="Search student by name or admission no"
                searchPlaceholder="Type student name / admission no"
                loading={studentsLoading}
                testid="select-student"
              />
            </div>
          </div>

          {selectedSessionId && !studentsLoading && students.length === 0 && (
            <div className="text-xs text-slate-500 py-3 text-center border border-dashed rounded mb-3">
              No students found for the selected filters.
            </div>
          )}

          {assigns.length === 0 && studentId && (
            <div className="text-xs text-slate-500 py-6 text-center border border-dashed rounded">
              No pending fees for this student.
            </div>
          )}

          {assigns.length > 0 && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                    Amount To Distribute
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    value={collectionAmount}
                    onChange={(e) => setDistributedAmount(e.target.value)}
                    placeholder="Enter total amount"
                    className="h-9 text-sm"
                    data-testid="collect-distribute-amount"
                  />
                </div>
                <div className="md:col-span-2 flex items-end">
                  <div className="text-xs text-slate-500 pb-2">
                    Enter one amount here. Pending dues will auto-fill from top
                    to bottom.
                  </div>
                </div>
              </div>
              <div className="border border-slate-200 rounded">
                <table className="w-full border-collapse text-sm">
                  <thead className="bg-slate-100 text-xs uppercase text-slate-700">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Fee Head</th>
                      <th className="px-2 py-1.5 text-left">Date</th>
                      <th className="px-2 py-1.5 text-right">Total</th>
                      <th className="px-2 py-1.5 text-right">Paid</th>
                      <th className="px-2 py-1.5 text-right">Due</th>
                      <th className="px-2 py-1.5 text-right w-32">Pay Now</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l, i) => {
                      const a = assigns[i];
                      return (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="px-2 py-1.5">
                            {a.narration || a.fee_type_id || "Fee"}
                          </td>
                          <td className="px-2 py-1.5 text-slate-600">
                            {formatDisplayDate(a.due_date)}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono">
                            {Number(a.amount).toFixed(2)}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono text-slate-500">
                            {Number(a.paid || 0).toFixed(2)}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono text-red-600">
                            {Number(a.due).toFixed(2)}
                          </td>
                          <td className="px-2 py-1.5">
                            <Input
                              type="number"
                              value={l.amount}
                              onChange={(e) => setAmount(i, e.target.value)}
                              className="h-7 text-xs text-right"
                              data-testid={`pay-amount-${i}`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="border border-slate-200 bg-white rounded-md p-3 space-y-3 h-fit">
          <h3 className="font-semibold text-sm">Payment</h3>
          {stu && (
            <div className="bg-slate-50 rounded p-2 text-xs">
              <div className="font-semibold">
                {stu.first_name} {stu.last_name}
              </div>
              <div className="text-slate-500">
                {stu.admission_no} · {stu.mobile || "—"}
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs font-semibold text-slate-700 mb-1 block">
              Receipt Date
            </Label>
            <Input
              type="date"
              value={receiptDate}
              onChange={(e) => setReceiptDate(e.target.value)}
              className="h-9 text-xs"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700 mb-1 block">
              Payment Mode
            </Label>
            <select
              className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
              value={modeLedger}
              onChange={(e) => setModeLedger(e.target.value)}
              data-testid="select-mode"
            >
              <option value="">— Select cash / bank account —</option>
              {paymentLedgers.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} [{inferLedgerMode(l) === "cash" ? "Cash" : "Bank"}]
                </option>
              ))}
            </select>
            <div className="text-[11px] text-slate-500 mt-1">
              Only cash and bank ledgers are shown for tally-style collection.
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700 mb-1 block">
              Payment Type
            </Label>
            <select
              className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
              value={instrumentType}
              onChange={(e) => setInstrumentType(e.target.value)}
              disabled={modeKind === "cash"}
            >
              {paymentOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {modeKind === "bank" && (
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                Bank Name
              </Label>
              <Input
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                className="h-9 text-xs"
              />
            </div>
          )}
          {instrumentType === "cheque" ? (
            <>
              <div>
                <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                  Cheque No
                </Label>
                <Input
                  value={chequeNo}
                  onChange={(e) => setChequeNo(e.target.value)}
                  className="h-9 text-xs"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                  Cheque Date
                </Label>
                <Input
                  type="date"
                  value={chequeDate}
                  onChange={(e) => setChequeDate(e.target.value)}
                  className="h-9 text-xs"
                />
              </div>
            </>
          ) : modeKind === "bank" ? (
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                UTR / Reference
              </Label>
              <Input
                value={utrNo}
                onChange={(e) => setUtrNo(e.target.value)}
                className="h-9 text-xs"
              />
            </div>
          ) : null}
          <div>
            <Label className="text-xs font-semibold text-slate-700 mb-1 block">
              Manual Reference
            </Label>
            <Input
              value={referenceNo}
              onChange={(e) => setReferenceNo(e.target.value)}
              className="h-9 text-xs"
              data-testid="reference-no"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700 mb-1 block">
              Remarks
            </Label>
            <Input
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="h-9 text-xs"
              data-testid="remarks"
            />
          </div>
          <div className="border-t pt-3 flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-700">Total</span>
            <span
              className="text-xl font-bold tracking-tight"
              data-testid="total-amount"
            >
              ₹{total.toFixed(2)}
            </span>
          </div>
          <Button
            onClick={submit}
            disabled={busy || total === 0 || !permissions.canAdd}
            className="w-full bg-slate-900 hover:bg-slate-800"
            data-testid="collect-submit"
          >
            {busy ? "Processing…" : `Collect ₹${total.toFixed(2)}`}
          </Button>

          {lastReceipt && (
            <div className="border border-emerald-300 bg-emerald-50 rounded p-2 text-xs">
              <div className="font-semibold text-emerald-700">
                Receipt {lastReceipt.receipt_no}
              </div>
              <div className="text-slate-600">
                Mode {lastReceipt.mode_ledger_name || lastReceipt.mode}
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={() =>
                    openAuthedReport(
                      `/api/fees/receipt/${lastReceipt.id}/pdf?format=html&autoprint=1`,
                    )
                  }
                  className="text-xs px-2 py-1 rounded bg-emerald-600 text-white"
                >
                  Print A4 Receipt
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await downloadAuthedReport(
                        `/api/fees/receipt/${lastReceipt.id}/pdf`,
                        `fee-receipt-${lastReceipt.receipt_no || lastReceipt.id}.pdf`,
                      );
                    } catch (error) {
                      toast.error(error.message || "Download failed");
                    }
                  }}
                  className="text-xs px-2 py-1 rounded border border-emerald-300 text-emerald-700 bg-white"
                >
                  Download PDF
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await shareAuthedPdfOnWhatsApp(
                        `/api/fees/receipt/${lastReceipt.id}/pdf`,
                        `fee-receipt-${lastReceipt.receipt_no || lastReceipt.id}.pdf`,
                        `Fee Receipt ${lastReceipt.receipt_no || ""}`.trim(),
                      );
                    } catch (error) {
                      toast.error(error.message || "WhatsApp share failed");
                    }
                  }}
                  className="text-xs px-2 py-1 rounded border border-sky-300 text-sky-700 bg-white"
                  title="Share on WhatsApp"
                  aria-label="Share on WhatsApp"
                >
                  <span className="inline-flex items-center gap-1">
                    <MessageCircle className="h-3.5 w-3.5" />
                    WhatsApp
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReceiptsList() {
  const permissions = useRouteModulePermissions("fees", "receipts");
  const { branchId, sessionId } = useAuth();
  const [rows, setRows] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [filters, setFilters] = useState({
    classId: "",
    section: "",
    studentId: "",
    fromDate: "",
    toDate: "",
    query: "",
  });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({
    date: "",
    instrument_type: "cash",
    reference_no: "",
    bank_name: "",
    cheque_no: "",
    cheque_date: "",
    utr_no: "",
    remarks: "",
  });
  const [confirmAction, setConfirmAction] = useState(null);
  const [metaLoading, setMetaLoading] = useState(true);

  const load = useCallback(async () => {
    const { data } = await api.get("/fees/receipt", {
      params: {
        branch_id: branchId,
        session_id: sessionId,
        class_id: filters.classId || undefined,
        section: filters.section || undefined,
        student_id: filters.studentId || undefined,
        from_date: filters.fromDate || undefined,
        to_date: filters.toDate || undefined,
        q: filters.query || undefined,
      },
    });
    setRows(data.items || []);
  }, [
    branchId,
    filters.classId,
    filters.fromDate,
    filters.query,
    filters.section,
    filters.studentId,
    filters.toDate,
    sessionId,
  ]);

  const loadMeta = useCallback(async () => {
    setMetaLoading(true);
    try {
      const [classRes, studentRes] = await Promise.all([
        api.get("/classes", { params: { branch_id: branchId, limit: 500 } }),
        api.get("/students", {
          params: {
            branch_id: branchId,
            session_id: sessionId,
            class_id: filters.classId || undefined,
            section: filters.section || undefined,
            limit: 1000,
          },
        }),
      ]);
      setClasses(classRes.data.items || []);
      setStudents(studentRes.data.items || []);
    } finally {
      setMetaLoading(false);
    }
  }, [branchId, filters.classId, filters.section, sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, studentId: "" }));
  }, [filters.classId, filters.section]);

  const selectedClass = classes.find(
    (item) => String(item.id) === String(filters.classId),
  );
  const sectionOptions = selectedClass?.sections || [];
  const studentOptions = studentComboboxOptions(students);

  const exportRows = () => {
    if (rows.length === 0) {
      toast.info("Nothing to export");
      return;
    }
    downloadCSV(
      rows,
      [
        {
          key: "receipt_voucher",
          label: "Receipt / Voucher",
          csv: (r) => `${r.receipt_no || ""} / ${r.voucher_no || ""}`,
        },
        { key: "date", label: "Date", csv: (r) => formatDisplayDate(r.date) },
        {
          key: "student_name",
          label: "Student",
          csv: (r) =>
            `${r.student_name || ""}${r.admission_no ? ` (${r.admission_no})` : ""}`,
        },
        {
          key: "class_id",
          label: "Class",
          csv: (r) =>
            classes.find((c) => String(c.id) === String(r.class_id))?.name ||
            classes.find((c) => String(c.id) === String(r.class_id))
              ?.class_name ||
            r.class_id ||
            "",
        },
        { key: "section", label: "Section" },
        { key: "mode_ledger_name", label: "Mode" },
        { key: "instrument_type", label: "Instrument" },
        { key: "reference_no", label: "Reference" },
        { key: "instrument_status", label: "Status" },
        {
          key: "deposited_date",
          label: "Deposited",
          csv: (r) => formatDisplayDate(r.deposited_date),
        },
        {
          key: "clearance_date",
          label: "Cleared",
          csv: (r) => formatDisplayDate(r.clearance_date),
        },
        {
          key: "total",
          label: "Amount",
          csv: (r) => Number(r.total || 0).toFixed(2),
        },
      ],
      "fee_receipts.csv",
    );
    toast.success("Exported");
  };

  const openPdf = (id) => {
    openAuthedReport(`/api/fees/receipt/${id}/pdf`);
  };
  const startEdit = (row) => {
    setEditId(row.id);
    setEditForm({
      date: row.date || "",
      instrument_type: row.instrument_type || "cash",
      reference_no: row.reference_no || "",
      bank_name: row.bank_name || "",
      cheque_no: row.cheque_no || "",
      cheque_date: row.cheque_date || "",
      utr_no: row.utr_no || "",
      remarks: row.remarks || "",
    });
  };
  const saveEdit = async () => {
    if (!permissions.canEdit) {
      toast.error("You do not have edit permission for receipts");
      return;
    }
    if (!editId) return;
    await api.put(`/fees/receipt/${editId}`, editForm);
    toast.success("Receipt updated");
    setEditId(null);
    await load();
  };
  const runConfirmedAction = async () => {
    if (!confirmAction) return;
    const action = confirmAction;
    setConfirmAction(null);
    if (action.type === "reconcile") {
      if (!permissions.canEdit) {
        toast.error("You do not have edit permission for receipts");
        return;
      }
      await api.post(`/fees/receipt/${action.row.id}/reconcile`, {
        status: action.status,
        notes: `${action.status} from fees receipt list`,
      });
      toast.success(
        `Receipt marked ${action.status === "deposited" ? "deposited" : "cleared"}`,
      );
    }
    if (action.type === "bounce") {
      if (!permissions.canEdit) {
        toast.error("You do not have edit permission for receipts");
        return;
      }
      await api.post(`/fees/receipt/${action.row.id}/bounce`, {
        bounce_date: todayIsoDate(),
        bounce_reason: "Cheque bounce",
      });
      toast.success("Cheque bounce reversed");
    }
    if (action.type === "delete") {
      if (!permissions.canDelete) {
        toast.error("You do not have delete permission for receipts");
        return;
      }
      await api.delete(`/fees/receipt/${action.row.id}`);
      toast.success("Receipt deleted");
    }
    await load();
  };
  return (
    <div data-testid="receipts-list">
      <PageTitle
        title="Receipts"
        subtitle="All collected receipts with A4 receipt print, bank details and cheque status"
        actions={[
          <Button
            key="export"
            type="button"
            variant="outline"
            onClick={exportRows}
          >
            Excel Export
          </Button>,
        ]}
      />

      <ReceiptActionDialog
        open={Boolean(confirmAction)}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null);
        }}
        title={confirmAction?.title || "Confirm action"}
        description={confirmAction?.description || ""}
        actionLabel={confirmAction?.actionLabel || "Continue"}
        actionTone={confirmAction?.actionTone || "default"}
        onConfirm={runConfirmedAction}
      />

      <div className="border border-slate-200 bg-white rounded-md p-3 mb-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3">
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1 block">
            Class
          </Label>
          <select
            className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white disabled:opacity-60 disabled:cursor-wait"
            value={filters.classId}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                classId: e.target.value,
                section: "",
                studentId: "",
              }))
            }
            disabled={metaLoading}
          >
            <option value="">{metaLoading ? "Loading classes…" : "All Classes"}</option>
            {!metaLoading &&
              classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || c.class_name || `Class ${c.id}`}
                </option>
              ))}
          </select>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1 block">
            Section
          </Label>
          <select
            className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white disabled:opacity-60 disabled:cursor-wait"
            value={filters.section}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                section: e.target.value,
                studentId: "",
              }))
            }
            disabled={metaLoading}
          >
            <option value="">{metaLoading ? "Loading…" : "All Sections"}</option>
            {!metaLoading &&
              sectionOptions.map((section) => (
                <option key={section} value={section}>
                  {section}
                </option>
              ))}
          </select>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1 block">
            Student
          </Label>
          <Combobox
            value={String(filters.studentId || "")}
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, studentId: value }))
            }
            options={studentOptions}
            placeholder="All students"
            searchPlaceholder="Search student"
            loading={metaLoading}
            testid="receipt-filter-student"
          />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1 block">
            From Date
          </Label>
          <Input
            type="date"
            value={filters.fromDate}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, fromDate: e.target.value }))
            }
          />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1 block">
            To Date
          </Label>
          <Input
            type="date"
            value={filters.toDate}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, toDate: e.target.value }))
            }
          />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1 block">
            Receipt / Voucher
          </Label>
          <Input
            value={filters.query}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, query: e.target.value }))
            }
            placeholder="Search receipt or voucher"
          />
        </div>
      </div>

      {editId && (
        <div className="border border-blue-200 bg-blue-50 rounded-md p-3 mb-3">
          <div className="text-sm font-semibold text-slate-800 mb-3">
            Edit Receipt Details
          </div>
          <div className="text-xs text-slate-500 mb-3">
            For full amount or allocation changes, delete this receipt and
            create it again with the correct details.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                Date
              </Label>
              <Input
                type="date"
                value={editForm.date}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, date: e.target.value }))
                }
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                Instrument
              </Label>
              <select
                className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
                value={editForm.instrument_type}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    instrument_type: e.target.value,
                  }))
                }
              >
                {[
                  { value: "cash", label: "Cash" },
                  { value: "bank_transfer", label: "Bank Transfer" },
                  { value: "upi", label: "UPI" },
                  { value: "neft", label: "NEFT" },
                  { value: "rtgs", label: "RTGS" },
                  { value: "imps", label: "IMPS" },
                  { value: "cheque", label: "Cheque" },
                ].map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                Reference
              </Label>
              <Input
                value={editForm.reference_no}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    reference_no: e.target.value,
                  }))
                }
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                Bank Name
              </Label>
              <Input
                value={editForm.bank_name}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    bank_name: e.target.value,
                  }))
                }
              />
            </div>
            {editForm.instrument_type === "cheque" ? (
              <>
                <div>
                  <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                    Cheque No
                  </Label>
                  <Input
                    value={editForm.cheque_no}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        cheque_no: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                    Cheque Date
                  </Label>
                  <Input
                    type="date"
                    value={editForm.cheque_date}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        cheque_date: e.target.value,
                      }))
                    }
                  />
                </div>
              </>
            ) : null}
            {editForm.instrument_type !== "cash" &&
            editForm.instrument_type !== "cheque" ? (
              <div>
                <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                  UTR / Reference
                </Label>
                <Input
                  value={editForm.utr_no}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, utr_no: e.target.value }))
                  }
                />
              </div>
            ) : null}
            <div className="md:col-span-2 xl:col-span-4">
              <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                Remarks
              </Label>
              <Input
                value={editForm.remarks}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, remarks: e.target.value }))
                }
              />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <Button type="button" onClick={saveEdit}>
              Save Changes
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditId(null)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <DataTable
        columns={[
          {
            key: "receipt_no",
            label: "Receipt No",
            render: (r) => (
              <div className="leading-tight">
                <div className="font-semibold">{r.receipt_no}</div>
              </div>
            ),
          },
          {
            key: "date",
            label: "Date",
            render: (r) => formatDisplayDate(r.date),
          },
          {
            key: "student_name",
            label: "Student",
            render: (r) => (
              <div className="leading-tight">
                <div className="font-medium">{r.student_name || "-"}</div>
                <div className="text-[11px] text-slate-500">
                  {r.admission_no || ""}
                </div>
              </div>
            ),
          },
          {
            key: "class_id",
            label: "Class",
            render: (r) =>
              classes.find((c) => String(c.id) === String(r.class_id))?.name ||
              classes.find((c) => String(c.id) === String(r.class_id))
                ?.class_name ||
              r.class_id ||
              "-",
          },
          { key: "section", label: "Section" },
          { key: "mode_ledger_name", label: "Mode" },
          { key: "instrument_type", label: "Instrument" },
          { key: "reference_no", label: "Ref" },
          { key: "instrument_status", label: "Status" },
          {
            key: "deposited_date",
            label: "Deposited",
            render: (r) => formatDisplayDate(r.deposited_date),
          },
          {
            key: "clearance_date",
            label: "Cleared",
            render: (r) => formatDisplayDate(r.clearance_date),
          },
          {
            key: "total",
            label: "Amount",
            render: (r) => (
              <span className="font-mono font-semibold erp-text-primary">
                ₹{Number(r.total).toFixed(2)}
              </span>
            ),
          },
          {
            key: "actions",
            label: "Actions",
            render: (r) => (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={async () => {
                    try {
                      await downloadAuthedReport(
                        `/api/fees/receipt/${r.id}/pdf`,
                        `fee-receipt-${r.receipt_no || r.id}.pdf`,
                      );
                    } catch (error) {
                      toast.error(error.message || "Download failed");
                    }
                  }}
                  className="text-xs px-2 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 font-semibold"
                  data-testid={`pdf-${r.id}`}
                >
                  PDF
                </button>
                <button
                  onClick={() =>
                    openAuthedReport(
                      `/api/fees/receipt/${r.id}/pdf?format=html&autoprint=1`,
                    )
                  }
                  className="text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 font-semibold inline-flex items-center gap-1"
                  title="Print receipt"
                >
                  <Printer className="h-3.5 w-3.5" />
                  Print
                </button>
                <button
                  onClick={async () => {
                    try {
                      await shareAuthedPdfOnWhatsApp(
                        `/api/fees/receipt/${r.id}/pdf`,
                        `fee-receipt-${r.receipt_no || r.id}.pdf`,
                        `Fee Receipt ${r.receipt_no || ""}`.trim(),
                      );
                    } catch (error) {
                      toast.error(error.message || "WhatsApp share failed");
                    }
                  }}
                  className="h-7 w-7 inline-flex items-center justify-center rounded bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100"
                  title="Share on WhatsApp"
                  aria-label="Share on WhatsApp"
                >
                  <MessageCircle className="h-3.5 w-3.5" />
                </button>
                {permissions.canEdit && (
                  <button
                    onClick={() => startEdit(r)}
                    className="text-xs px-2 py-0.5 rounded bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100 font-semibold"
                  >
                    Edit
                  </button>
                )}
                {permissions.canDelete && (
                  <button
                    onClick={() =>
                      setConfirmAction({
                        type: "delete",
                        row: r,
                        title: `Delete receipt ${r.receipt_no}?`,
                        description:
                          "This will remove the receipt and its voucher posting. For full receipt changes, delete the receipt and create it again with the correct details.",
                        actionLabel: "Delete Receipt",
                        actionTone: "danger",
                      })
                    }
                    className="text-xs px-2 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 font-semibold"
                  >
                    Delete
                  </button>
                )}
                {String(r.instrument_type || "") === "cheque" &&
                nextChequeStatus(String(r.instrument_status || "")) &&
                permissions.canEdit ? (
                  <button
                    onClick={() => {
                      const nextStatus = nextChequeStatus(
                        String(r.instrument_status || ""),
                      );
                      setConfirmAction({
                        type: "reconcile",
                        row: r,
                        status: nextStatus,
                        title:
                          nextStatus === "deposited"
                            ? `Deposit cheque ${r.receipt_no}?`
                            : `Clear cheque ${r.receipt_no}?`,
                        description:
                          nextStatus === "deposited"
                            ? "This will mark the cheque as deposited and store today's deposit date by default."
                            : "This will mark the cheque as cleared and store today's clearance date by default.",
                        actionLabel:
                          nextStatus === "deposited"
                            ? "Confirm Deposit"
                            : "Confirm Clear",
                        actionTone:
                          nextStatus === "deposited" ? "default" : "success",
                      });
                    }}
                    className="text-xs px-2 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100 font-semibold"
                  >
                    {String(r.instrument_status || "") === "pending"
                      ? "Deposit"
                      : "Clear"}
                  </button>
                ) : null}
                {String(r.instrument_type || "") === "cheque" &&
                String(r.instrument_status || "") !== "bounced" &&
                permissions.canEdit ? (
                  <button
                    onClick={() =>
                      setConfirmAction({
                        type: "bounce",
                        row: r,
                        title: `Bounce cheque ${r.receipt_no}?`,
                        description:
                          "This will reverse the cheque receipt effect and mark the instrument as bounced.",
                        actionLabel: "Confirm Bounce",
                        actionTone: "danger",
                      })
                    }
                    className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 font-semibold"
                  >
                    Bounce
                  </button>
                ) : null}
              </div>
            ),
          },
        ]}
        rows={rows}
      />
    </div>
  );
}

function ChequeReconciliationPage() {
  const permissions = useRouteModulePermissions("fees", "reconciliation");
  const { branchId, sessionId } = useAuth();
  const [rows, setRows] = useState([]);
  const [classes, setClasses] = useState([]);
  const [confirmAction, setConfirmAction] = useState(null);

  const load = async () => {
    const [{ data }, classRes] = await Promise.all([
      api.get("/fees/receipt", {
        params: {
          branch_id: branchId,
          session_id: sessionId,
          instrument_type: "cheque",
        },
      }),
      api.get("/classes", { params: { branch_id: branchId, limit: 500 } }),
    ]);
    setRows(data.items || []);
    setClasses(classRes.data.items || []);
  };

  useEffect(() => {
    load();
  }, [branchId, sessionId]);

  const runConfirmedAction = async () => {
    if (!confirmAction) return;
    const action = confirmAction;
    setConfirmAction(null);
    if (action.type === "reconcile") {
      if (!permissions.canEdit) {
        toast.error("You do not have permission for cheque reconciliation");
        return;
      }
      await api.post(`/fees/receipt/${action.row.id}/reconcile`, {
        status: action.status,
        notes: `Cheque ${action.status} from reconciliation page`,
      });
      toast.success(`Cheque marked ${action.status}`);
    }
    if (action.type === "bounce") {
      if (!permissions.canEdit) {
        toast.error("You do not have permission for cheque reconciliation");
        return;
      }
      await api.post(`/fees/receipt/${action.row.id}/bounce`, {
        bounce_date: todayIsoDate(),
        bounce_reason: "Cheque bounce",
      });
      toast.success("Cheque bounce reversed");
    }
    await load();
  };

  const classLabel = (row) =>
    classes.find((c) => String(c.id) === String(row.class_id))?.name ||
    classes.find((c) => String(c.id) === String(row.class_id))?.class_name ||
    row.class_id ||
    "-";

  return (
    <div data-testid="cheque-reconciliation-page">
      <PageTitle
        title="Cheque Reconciliation"
        subtitle="Track deposited, cleared and bounced cheque receipts in a tally-style flow"
      />
      <ReceiptActionDialog
        open={Boolean(confirmAction)}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null);
        }}
        title={confirmAction?.title || "Confirm action"}
        description={confirmAction?.description || ""}
        actionLabel={confirmAction?.actionLabel || "Continue"}
        actionTone={confirmAction?.actionTone || "default"}
        onConfirm={runConfirmedAction}
      />
      <DataTable
        columns={[
          { key: "receipt_no", label: "Receipt #" },
          {
            key: "date",
            label: "Date",
            render: (r) => formatDisplayDate(r.date),
          },
          {
            key: "student_name",
            label: "Student",
            render: (r) => (
              <div className="leading-tight">
                <div className="font-medium">{r.student_name || "-"}</div>
                <div className="text-[11px] text-slate-500">
                  {r.admission_no || ""}
                </div>
              </div>
            ),
          },
          {
            key: "class_id",
            label: "Class",
            render: (r) => classLabel(r),
          },
          {
            key: "section",
            label: "Section",
            render: (r) => r.section || "-",
          },
          { key: "mode_ledger_name", label: "Bank Ledger" },
          { key: "cheque_no", label: "Cheque No" },
          { key: "bank_name", label: "Bank" },
          { key: "instrument_status", label: "Status" },
          {
            key: "deposited_date",
            label: "Deposited",
            render: (r) => formatDisplayDate(r.deposited_date),
          },
          {
            key: "clearance_date",
            label: "Cleared",
            render: (r) => formatDisplayDate(r.clearance_date),
          },
          {
            key: "amount",
            label: "Amount",
            render: (r) => `Rs. ${Number(r.amount || r.total || 0).toFixed(2)}`,
          },
          {
            key: "actions",
            label: "Actions",
            render: (r) => (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await downloadAuthedReport(
                        `/api/fees/receipt/${r.id}/pdf`,
                        `fee-receipt-${r.receipt_no || r.id}.pdf`,
                      );
                    } catch (error) {
                      toast.error(error.message || "Download failed");
                    }
                  }}
                  className="text-xs px-2 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 font-semibold"
                >
                  PDF
                </button>
                {String(r.instrument_status || "") === "pending" &&
                permissions.canEdit ? (
                  <button
                    type="button"
                    onClick={() =>
                      setConfirmAction({
                        type: "reconcile",
                        row: r,
                        status: "deposited",
                        title: `Deposit cheque ${r.receipt_no}?`,
                        description:
                          "This will mark the cheque as deposited with today's deposit date by default.",
                        actionLabel: "Confirm Deposit",
                        actionTone: "default",
                      })
                    }
                    className="text-xs px-2 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100 font-semibold"
                  >
                    Deposit
                  </button>
                ) : null}
                {String(r.instrument_status || "") === "deposited" &&
                permissions.canEdit ? (
                  <button
                    type="button"
                    onClick={() =>
                      setConfirmAction({
                        type: "reconcile",
                        row: r,
                        status: "cleared",
                        title: `Clear cheque ${r.receipt_no}?`,
                        description:
                          "This will mark the cheque as cleared with today's clearance date by default.",
                        actionLabel: "Confirm Clear",
                        actionTone: "success",
                      })
                    }
                    className="text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 font-semibold"
                  >
                    Clear
                  </button>
                ) : null}
                {String(r.instrument_status || "") !== "bounced" &&
                permissions.canEdit ? (
                  <button
                    type="button"
                    onClick={() =>
                      setConfirmAction({
                        type: "bounce",
                        row: r,
                        title: `Bounce cheque ${r.receipt_no}?`,
                        description:
                          "This will reverse the cheque receipt effect and mark it as bounced.",
                        actionLabel: "Confirm Bounce",
                        actionTone: "danger",
                      })
                    }
                    className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 font-semibold"
                  >
                    Bounce
                  </button>
                ) : null}
              </div>
            ),
          },
        ]}
        rows={rows}
      />
    </div>
  );
}

function AssignFee() {
  const permissions = useRouteModulePermissions("fees", "assign");
  const { branchId, sessionId } = useAuth();
  const [classes, setClasses] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [classId, setClassId] = useState("");
  const [students, setStudents] = useState([]);
  const [feeMasters, setFeeMasters] = useState([]);
  const [studentIds, setStudentIds] = useState([]);
  const [feeMasterIds, setFeeMasterIds] = useState([]);
  const [assignDate, setAssignDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [debitLedgerId, setDebitLedgerId] = useState("");
  const [assigns, setAssigns] = useState([]);
  const [editAssignId, setEditAssignId] = useState(null);
  const [editAssign, setEditAssign] = useState({
    amount: "",
    due_date: "",
    status: "pending",
    narration: "",
  });
  const [listClassFilter, setListClassFilter] = useState("");
  const [listStudentFilter, setListStudentFilter] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    setDataLoading(true);
    (async () => {
      try {
        const [c, l, s, fm, fa] = await Promise.all([
          api.get("/classes", { params: { branch_id: branchId } }),
          api.get("/ledgers", { params: { branch_id: branchId, limit: 1000 } }),
          api.get("/students", {
            params: { branch_id: branchId, session_id: sessionId, limit: 1000 },
          }),
          api.get("/fee-master", {
            params: { branch_id: branchId, session_id: sessionId, limit: 2000 },
          }),
          api.get("/fees/assign", {
            params: { branch_id: branchId, session_id: sessionId },
          }),
        ]);
        const classItems = c.data.items || [];
        setClasses(classItems);
        setLedgers(l.data.items || []);
        setStudents(s.data.items || []);
        setFeeMasters(fm.data.items || []);
        setAssigns(fa.data.items || []);
        setClassId((prev) =>
          prev && classItems.some((item) => String(item.id) === String(prev))
            ? prev
            : String(classItems[0]?.id || ""),
        );
      } finally {
        setDataLoading(false);
      }
    })();
  }, [branchId, sessionId]);

  const toggle = (list, setList, id) => {
    if (list.includes(id)) setList(list.filter((x) => x !== id));
    else setList([...list, id]);
  };

  const filteredStudents = classId
    ? students.filter((s) => String(s.class_id || "") === String(classId))
    : students;
  const filteredFeeMasters = classId
    ? feeMasters.filter((f) => String(f.class_id || "") === String(classId))
    : feeMasters;

  const installmentLabel = (frequency) => {
    const f = String(frequency || "monthly").toLowerCase();
    if (f === "monthly") return "12 installments";
    if (f === "quarterly") return "4 installments";
    return "1 installment";
  };

  const selectAllVisibleStudents = () => {
    const ids = filteredStudents.map((s) => s.id);
    setStudentIds(ids);
  };

  const selectAllVisibleMasters = () => {
    const ids = filteredFeeMasters.map((f) => f.id);
    setFeeMasterIds(ids);
  };

  const doAssign = async () => {
    if (!permissions.canAdd) {
      toast.error("You do not have assign permission");
      return;
    }
    if (studentIds.length === 0 || feeMasterIds.length === 0)
      return toast.error("Select students and fee masters");
    const { data } = await api.post("/fees/assign", {
      student_ids: studentIds,
      fee_master_ids: feeMasterIds,
      branch_id: branchId,
      session_id: sessionId,
      assign_date: assignDate,
      debit_ledger_id: debitLedgerId || null,
    });
    toast.success(
      `${data.created || 0} assignments created, ${data.skipped || 0} skipped`,
    );
    setStudentIds([]);
    setFeeMasterIds([]);
    const { data: listData } = await api.get("/fees/assign", {
      params: { branch_id: branchId, session_id: sessionId },
    });
    setAssigns(listData.items || []);
  };

  const startEditAssign = (row) => {
    setEditAssignId(row.id);
    setEditAssign({
      amount: String(row.amount ?? ""),
      due_date: row.due_date || "",
      status: row.status || "pending",
      narration: row.narration || "",
    });
  };

  const updateAssign = async () => {
    if (!permissions.canEdit) {
      toast.error("You do not have edit permission for assignments");
      return;
    }
    if (!editAssignId) return;
    await api.put(`/fees/assign/${editAssignId}`, {
      amount: Number(editAssign.amount || 0),
      due_date: editAssign.due_date || null,
      status: editAssign.status,
      narration: editAssign.narration,
    });
    toast.success("Assignment updated");
    setEditAssignId(null);
    const { data } = await api.get("/fees/assign", {
      params: { branch_id: branchId, session_id: sessionId },
    });
    setAssigns(data.items || []);
  };

  const studentById = {};
  for (const s of students) studentById[String(s.id)] = s;

  const listFilterStudents = listClassFilter
    ? students.filter((s) => String(s.class_id || "") === String(listClassFilter))
    : students;

  const filteredAssigns = assigns.filter((r) => {
    if (listClassFilter) {
      const stu = studentById[String(r.student_id)];
      if (!stu || String(stu.class_id || "") !== String(listClassFilter))
        return false;
    }
    if (
      listStudentFilter &&
      String(r.student_id) !== String(listStudentFilter)
    )
      return false;
    return true;
  });

  const runConfirmedAction = async () => {
    if (!confirmAction) return;
    const action = confirmAction;
    setConfirmAction(null);
    if (action.type === "delete-assign") {
      if (!permissions.canDelete) {
        toast.error("You do not have delete permission for assignments");
        return;
      }
      try {
        await api.delete(`/fees/assign/${action.row.id}`);
        toast.success("Assignment deleted");
        setAssigns((prev) => prev.filter((a) => a.id !== action.row.id));
      } catch (error) {
        toast.error(
          error?.response?.data?.detail || "Failed to delete assignment",
        );
      }
    }
  };

  return (
    <div data-testid="assign-fee">
      <PageTitle
        title="Fee Assignment"
        subtitle="DR/CR based assignment with journal posting and installment auto-generation"
      />
      <ReceiptActionDialog
        open={Boolean(confirmAction)}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null);
        }}
        title={confirmAction?.title || "Confirm action"}
        description={confirmAction?.description || ""}
        actionLabel={confirmAction?.actionLabel || "Continue"}
        actionTone={confirmAction?.actionTone || "default"}
        onConfirm={runConfirmedAction}
      />
      <div className="border border-slate-200 bg-white rounded-md p-3 mb-3 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1 block">
            Class Filter
          </Label>
          <select
            className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white disabled:opacity-60 disabled:cursor-wait"
            value={classId}
            onChange={(e) => {
              setClassId(e.target.value);
              setStudentIds([]);
              setFeeMasterIds([]);
            }}
            disabled={dataLoading}
          >
            {dataLoading ? (
              <option value="">Loading classes…</option>
            ) : (
              classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || c.class_name || `Class ${c.id}`}
                </option>
              ))
            )}
          </select>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1 block">
            Assign Date
          </Label>
          <Input
            type="date"
            value={assignDate}
            onChange={(e) => setAssignDate(e.target.value)}
          />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1 block">
            Debit Ledger (DR)
          </Label>
          <select
            className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
            value={debitLedgerId}
            onChange={(e) => setDebitLedgerId(e.target.value)}
          >
            <option value="">Auto (Fee Receivable/First Asset)</option>
            {ledgers.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name} {l.type ? `(${l.type})` : ""}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
        <div className="border border-slate-200 bg-white rounded-md p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold">
              Students ({studentIds.length} selected)
            </h3>
            <button
              className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50"
              onClick={selectAllVisibleStudents}
              type="button"
            >
              Select All Visible
            </button>
          </div>
          <div className="max-h-72 overflow-auto text-xs divide-y divide-slate-100">
            {filteredStudents.map((s) => (
              <label
                key={s.id}
                className="flex items-center gap-2 py-1 hover:bg-slate-50 px-1 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={studentIds.includes(s.id)}
                  onChange={() => toggle(studentIds, setStudentIds, s.id)}
                  data-testid={`assign-stu-${s.id}`}
                />
                <span>
                  {s.admission_no} — {s.first_name} {s.last_name}
                </span>
              </label>
            ))}
          </div>
        </div>
        <div className="border border-slate-200 bg-white rounded-md p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold">
              Fee Masters ({feeMasterIds.length} selected)
            </h3>
            <button
              className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50"
              onClick={selectAllVisibleMasters}
              type="button"
            >
              Select All Visible
            </button>
          </div>
          <div className="max-h-72 overflow-auto text-xs divide-y divide-slate-100">
            {filteredFeeMasters.map((f) => (
              <label
                key={f.id}
                className="flex items-center gap-2 py-1 hover:bg-slate-50 px-1 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={feeMasterIds.includes(f.id)}
                  onChange={() => toggle(feeMasterIds, setFeeMasterIds, f.id)}
                  data-testid={`assign-fm-${f.id}`}
                />
                <span>
                  {f.name} — ₹{f.amount} · {installmentLabel(f.frequency)}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>
      <Button
        onClick={doAssign}
        className="bg-slate-900 hover:bg-slate-800"
        data-testid="do-assign"
        disabled={!permissions.canAdd}
      >
        Assign Selected
      </Button>

      <div className="mt-4">
        <h3 className="text-sm font-semibold mb-2">All Assignments</h3>
        <div className="border border-slate-200 bg-white rounded-md p-3 mb-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-semibold text-slate-700 mb-1 block">
              Class Filter
            </Label>
            <select
              className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white disabled:opacity-60 disabled:cursor-wait"
              value={listClassFilter}
              onChange={(e) => {
                setListClassFilter(e.target.value);
                setListStudentFilter("");
              }}
              disabled={dataLoading}
              data-testid="assign-list-class-filter"
            >
              <option value="">{dataLoading ? "Loading classes…" : "All Classes"}</option>
              {!dataLoading &&
                classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name || c.class_name || `Class ${c.id}`}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700 mb-1 block">
              Student Filter
            </Label>
            <Combobox
              value={String(listStudentFilter || "")}
              onChange={(value) => setListStudentFilter(value)}
              options={studentComboboxOptions(listFilterStudents)}
              placeholder="All students"
              searchPlaceholder="Search student"
              loading={dataLoading}
              testid="assign-list-student-filter"
            />
          </div>
        </div>
        {editAssignId && (
          <div className="mb-3 border border-blue-200 bg-blue-50 rounded p-3 grid grid-cols-1 md:grid-cols-5 gap-2">
            <Input
              type="number"
              value={editAssign.amount}
              onChange={(e) =>
                setEditAssign({ ...editAssign, amount: e.target.value })
              }
              placeholder="Amount"
            />
            <Input
              type="date"
              value={editAssign.due_date}
              onChange={(e) =>
                setEditAssign({ ...editAssign, due_date: e.target.value })
              }
            />
            <select
              className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
              value={editAssign.status}
              onChange={(e) =>
                setEditAssign({ ...editAssign, status: e.target.value })
              }
            >
              <option value="pending">Pending</option>
              <option value="partial">Partial</option>
              <option value="paid">Paid</option>
            </select>
            <Input
              value={editAssign.narration}
              onChange={(e) =>
                setEditAssign({ ...editAssign, narration: e.target.value })
              }
              placeholder="Narration"
            />
            <div className="flex gap-2">
              <Button type="button" onClick={updateAssign}>
                Update
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditAssignId(null)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
        <DataTable
          columns={[
            {
              key: "student_id",
              label: "Student",
              render: (r) =>
                `${students.find((s) => s.id === r.student_id)?.first_name || ""} ${students.find((s) => s.id === r.student_id)?.last_name || ""}`.trim() ||
                r.student_id,
            },
            {
              key: "fee_master_id",
              label: "Fee Master",
              render: (r) =>
                feeMasters.find((f) => String(f.id) === String(r.fee_master_id))
                  ?.name ||
                r.narration ||
                r.fee_master_id,
            },
            { key: "amount", label: "Amount" },
            { key: "paid", label: "Paid" },
            {
              key: "due",
              label: "Due",
              render: (r) => (
                <span className="font-mono text-red-600">
                  ₹{Number(r.due).toFixed(2)}
                </span>
              ),
            },
            {
              key: "due_date",
              label: "Due Date",
              render: (r) => formatDisplayDate(r.due_date),
            },
            { key: "status", label: "Status" },
            {
              key: "actions",
              label: "Actions",
              render: (r) => (
                <div className="flex gap-2">
                  <button
                    className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50"
                    type="button"
                    onClick={() => startEditAssign(r)}
                    disabled={!permissions.canEdit}
                  >
                    Edit
                  </button>
                  {permissions.canDelete && (
                    <button
                      className="text-xs px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50"
                      type="button"
                      onClick={() =>
                        setConfirmAction({
                          type: "delete-assign",
                          row: r,
                          title: "Delete this fee assignment?",
                          description:
                            "This removes the assignment and its journal posting. Assignments with payments or concessions already recorded cannot be deleted.",
                          actionLabel: "Delete Assignment",
                          actionTone: "danger",
                        })
                      }
                    >
                      Delete
                    </button>
                  )}
                </div>
              ),
            },
          ]}
          rows={filteredAssigns}
        />
      </div>
    </div>
  );
}

function MiscFeeAssign() {
  const permissions = useRouteModulePermissions("fees", "misc_assign");
  const { branchId, sessionId } = useAuth();
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [students, setStudents] = useState([]);
  const [studentIds, setStudentIds] = useState([]);
  const [amount, setAmount] = useState("");
  const [assignDate, setAssignDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [remarks, setRemarks] = useState("");
  const [assigns, setAssigns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);

  const load = useCallback(async () => {
    setDataLoading(true);
    try {
      const [c, s, fa] = await Promise.all([
        api.get("/classes", { params: { branch_id: branchId } }),
        api.get("/students", {
          params: { branch_id: branchId, session_id: sessionId, limit: 1000 },
        }),
        api.get("/fees/assign", {
          params: { branch_id: branchId, session_id: sessionId },
        }),
      ]);
      const classItems = c.data.items || [];
      setClasses(classItems);
      setStudents(s.data.items || []);
      setAssigns(
        (fa.data.items || []).filter(
          (r) => r.fee_master_id === null || r.fee_master_id === "",
        ),
      );
      setClassId((prev) =>
        prev && classItems.some((item) => String(item.id) === String(prev))
          ? prev
          : String(classItems[0]?.id || ""),
      );
    } finally {
      setDataLoading(false);
    }
  }, [branchId, sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredStudents = classId
    ? students.filter((s) => String(s.class_id || "") === String(classId))
    : students;

  const toggle = (id) => {
    setStudentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const selectAllVisible = () => {
    setStudentIds(filteredStudents.map((s) => s.id));
  };

  const doAssign = async () => {
    if (!permissions.canAdd) {
      toast.error("You do not have assign permission");
      return;
    }
    if (studentIds.length === 0) {
      toast.error("Select at least one student");
      return;
    }
    const amt = Number(amount || 0);
    if (!(amt > 0)) {
      toast.error("Enter a valid fee amount");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/fees/assign/misc", {
        student_ids: studentIds,
        amount: amt,
        branch_id: branchId,
        session_id: sessionId,
        assign_date: assignDate,
        remarks: remarks.trim(),
      });
      toast.success(
        `Miscellaneous fee assigned to ${data.created || 0} student(s)`,
      );
      setStudentIds([]);
      setAmount("");
      setRemarks("");
      await load();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Failed to assign miscellaneous fee",
      );
    } finally {
      setBusy(false);
    }
  };

  const runConfirmedAction = async () => {
    if (!confirmAction) return;
    const action = confirmAction;
    setConfirmAction(null);
    if (action.type === "delete-misc") {
      if (!permissions.canDelete) {
        toast.error("You do not have delete permission for assignments");
        return;
      }
      try {
        await api.delete(`/fees/assign/${action.row.id}`);
        toast.success("Assignment deleted");
        setAssigns((prev) => prev.filter((a) => a.id !== action.row.id));
      } catch (error) {
        toast.error(
          error?.response?.data?.detail || "Failed to delete assignment",
        );
      }
    }
  };

  const studentName = (id) => {
    const s = students.find((item) => String(item.id) === String(id));
    return s
      ? `${s.admission_no ? s.admission_no + " — " : ""}${s.first_name || ""} ${s.last_name || ""}`.trim()
      : id;
  };

  return (
    <div data-testid="misc-fee-assign">
      <PageTitle
        title="Miscellaneous Fees"
        subtitle="Assign a one-off miscellaneous fee to selected students and post it to the journal"
      />
      <ReceiptActionDialog
        open={Boolean(confirmAction)}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null);
        }}
        title={confirmAction?.title || "Confirm action"}
        description={confirmAction?.description || ""}
        actionLabel={confirmAction?.actionLabel || "Continue"}
        actionTone={confirmAction?.actionTone || "default"}
        onConfirm={runConfirmedAction}
      />
      <div className="border border-slate-200 bg-white rounded-md p-3 mb-3 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1 block">
            Class Filter
          </Label>
          <select
            className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white disabled:opacity-60 disabled:cursor-wait"
            value={classId}
            onChange={(e) => {
              setClassId(e.target.value);
              setStudentIds([]);
            }}
            disabled={dataLoading}
          >
            {dataLoading ? (
              <option value="">Loading classes…</option>
            ) : (
              classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || c.class_name || `Class ${c.id}`}
                </option>
              ))
            )}
          </select>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1 block">
            Fee Head
          </Label>
          <Input value="Miscellaneous Fees" disabled readOnly />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1 block">
            Assign Date
          </Label>
          <Input
            type="date"
            value={assignDate}
            onChange={(e) => setAssignDate(e.target.value)}
          />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1 block">
            Fee Amount
          </Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Enter amount"
            data-testid="misc-amount"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs font-semibold text-slate-700 mb-1 block">
            Remarks
          </Label>
          <Input
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Optional remarks (e.g. reason for this fee)"
            data-testid="misc-remarks"
          />
        </div>
      </div>
      <div className="border border-slate-200 bg-white rounded-md p-3 mb-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold">
            Students ({studentIds.length} selected)
          </h3>
          <button
            className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50"
            onClick={selectAllVisible}
            type="button"
          >
            Select All Visible
          </button>
        </div>
        <div className="max-h-72 overflow-auto text-xs divide-y divide-slate-100">
          {filteredStudents.map((s) => (
            <label
              key={s.id}
              className="flex items-center gap-2 py-1 hover:bg-slate-50 px-1 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={studentIds.includes(s.id)}
                onChange={() => toggle(s.id)}
                data-testid={`misc-stu-${s.id}`}
              />
              <span>
                {s.admission_no} — {s.first_name} {s.last_name}
              </span>
            </label>
          ))}
        </div>
      </div>
      <Button
        onClick={doAssign}
        className="bg-slate-900 hover:bg-slate-800"
        data-testid="do-misc-assign"
        disabled={!permissions.canAdd || busy}
      >
        Assign Miscellaneous Fee
      </Button>

      <div className="mt-4">
        <h3 className="text-sm font-semibold mb-2">
          Miscellaneous Fee Assignments
        </h3>
        <DataTable
          columns={[
            {
              key: "student_id",
              label: "Student",
              render: (r) => studentName(r.student_id),
            },
            { key: "amount", label: "Amount" },
            { key: "paid", label: "Paid" },
            {
              key: "due",
              label: "Due",
              render: (r) => (
                <span className="font-mono text-red-600">
                  ₹{Number(r.due).toFixed(2)}
                </span>
              ),
            },
            {
              key: "due_date",
              label: "Date",
              render: (r) => formatDisplayDate(r.due_date),
            },
            { key: "status", label: "Status" },
            {
              key: "remarks",
              label: "Remarks",
              render: (r) =>
                String(r.narration || "")
                  .replace(/^Miscellaneous Fees\s*-\s*/, "")
                  .replace(/^Miscellaneous Fees$/, "") || "-",
            },
            {
              key: "actions",
              label: "Actions",
              render: (r) =>
                permissions.canDelete ? (
                  <button
                    className="text-xs px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50"
                    type="button"
                    onClick={() =>
                      setConfirmAction({
                        type: "delete-misc",
                        row: r,
                        title: "Delete this miscellaneous fee assignment?",
                        description:
                          "This removes the assignment and its journal posting. Assignments with payments or concessions already recorded cannot be deleted.",
                        actionLabel: "Delete Assignment",
                        actionTone: "danger",
                      })
                    }
                  >
                    Delete
                  </button>
                ) : null,
            },
          ]}
          rows={assigns}
        />
      </div>
    </div>
  );
}

function FeeTypesPage() {
  const permissions = useRouteModulePermissions("fees", "fee_types");
  const { branchId } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    name: "",
    code: "",
    frequency: "monthly",
    description: "",
  });

  const load = async () => {
    const { data } = await api.get("/fee-types", {
      params: { branch_id: branchId, limit: 500 },
    });
    setRows(data.items || []);
  };

  useEffect(() => {
    load();
  }, [branchId]);

  const save = async () => {
    if (editId && !permissions.canEdit) {
      toast.error("You do not have edit permission for fee types");
      return;
    }
    if (!editId && !permissions.canAdd) {
      toast.error("You do not have add permission for fee types");
      return;
    }
    if (!form.name.trim()) {
      return toast.error("Fee type name is required");
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        branch_id: branchId,
      };
      if (editId) {
        await api.put(`/fee-types/${editId}`, payload);
        toast.success("Fee type updated");
      } else {
        await api.post("/fee-types", payload);
        toast.success("Fee type saved");
      }
      setEditId(null);
      setForm({ name: "", code: "", frequency: "monthly", description: "" });
      await load();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!permissions.canDelete) {
      toast.error("You do not have delete permission for fee types");
      return;
    }
    await api.delete(`/fee-types/${id}`);
    toast.success("Fee type deleted");
    await load();
  };

  const startEdit = (row) => {
    setEditId(row.id);
    setForm({
      name: row.name || "",
      code: row.code || "",
      frequency: row.frequency || "monthly",
      description: row.description || "",
    });
  };

  return (
    <div data-testid="fee-types-custom">
      <PageTitle
        title="Fee Types"
        subtitle="Define fee type and frequency: monthly, quarterly, yearly"
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <div>
          <Label className="text-xs mb-1 block">Name</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Tuition Fee"
          />
        </div>
        <div>
          <Label className="text-xs mb-1 block">Code</Label>
          <Input
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            placeholder="TUITION"
          />
        </div>
        <div>
          <Label className="text-xs mb-1 block">Frequency</Label>
          <select
            className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
            value={form.frequency}
            onChange={(e) => setForm({ ...form, frequency: e.target.value })}
          >
            <option value="monthly">Monthly (12)</option>
            <option value="quarterly">Quarterly (4)</option>
            <option value="yearly">Yearly (1)</option>
          </select>
        </div>
      </div>
      <div className="mb-4">
        <Label className="text-xs mb-1 block">Description</Label>
        <Input
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Optional notes"
        />
      </div>
      <Button
        onClick={save}
        disabled={
          busy ||
          (!editId && !permissions.canAdd) ||
          (editId && !permissions.canEdit)
        }
        className="mb-4"
      >
        {busy ? "Saving..." : editId ? "Update Fee Type" : "Save Fee Type"}
      </Button>
      {editId && (
        <Button
          type="button"
          variant="outline"
          className="mb-4 ml-2"
          onClick={() => {
            setEditId(null);
            setForm({
              name: "",
              code: "",
              frequency: "monthly",
              description: "",
            });
          }}
        >
          Cancel Edit
        </Button>
      )}

      <DataTable
        columns={[
          { key: "name", label: "Name" },
          { key: "code", label: "Code" },
          { key: "frequency", label: "Frequency" },
          { key: "description", label: "Description" },
          {
            key: "actions",
            label: "Actions",
            render: (r) => (
              <div className="flex gap-2">
                {permissions.canEdit && (
                  <button
                    className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50"
                    onClick={() => startEdit(r)}
                    type="button"
                  >
                    Edit
                  </button>
                )}
                {permissions.canDelete && (
                  <button
                    className="text-xs px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50"
                    onClick={() => remove(r.id)}
                    type="button"
                  >
                    Delete
                  </button>
                )}
              </div>
            ),
          },
        ]}
        rows={rows}
      />
    </div>
  );
}

function FeeMasterPage() {
  const permissions = useRouteModulePermissions("fees", "fee_master");
  const { branchId, sessionId } = useAuth();
  const [classes, setClasses] = useState([]);
  const [feeTypes, setFeeTypes] = useState([]);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    class_id: "",
    fee_type_id: "",
    amount: "",
    frequency: "monthly",
  });

  const loadAll = async () => {
    const [c, t, m] = await Promise.all([
      api.get("/classes", { params: { branch_id: branchId, limit: 500 } }),
      api.get("/fee-types", { params: { branch_id: branchId, limit: 500 } }),
      api.get("/fee-master", {
        params: { branch_id: branchId, session_id: sessionId, limit: 2000 },
      }),
    ]);
    setClasses(c.data.items || []);
    setFeeTypes(t.data.items || []);
    setRows(m.data.items || []);
  };

  useEffect(() => {
    loadAll();
  }, [branchId, sessionId]);

  const save = async () => {
    if (editId && !permissions.canEdit) {
      toast.error("You do not have edit permission for fee master");
      return;
    }
    if (!editId && !permissions.canAdd) {
      toast.error("You do not have add permission for fee master");
      return;
    }
    if (!form.class_id || !form.fee_type_id || !form.amount) {
      return toast.error("Class, fee type and amount are required");
    }

    const selectedFeeType = feeTypes.find(
      (t) => String(t.id) === String(form.fee_type_id),
    );
    const autoHeadName = String(selectedFeeType?.name || "").trim();
    if (!autoHeadName) {
      return toast.error("Selected fee type is invalid");
    }

    setBusy(true);
    try {
      const payload = {
        name: autoHeadName,
        class_id: form.class_id,
        fee_type_id: form.fee_type_id,
        amount: Number(form.amount || 0),
        frequency: form.frequency,
        branch_id: branchId,
        session_id: sessionId,
      };
      if (editId) {
        await api.put(`/fee-master/${editId}`, payload);
        toast.success("Fee master updated");
      } else {
        await api.post("/fee-master", payload);
        toast.success("Fee master saved");
      }
      setEditId(null);
      setForm({
        class_id: "",
        fee_type_id: "",
        amount: "",
        frequency: "monthly",
      });
      await loadAll();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!permissions.canDelete) {
      toast.error("You do not have delete permission for fee master");
      return;
    }
    await api.delete(`/fee-master/${id}`);
    toast.success("Fee master deleted");
    await loadAll();
  };

  const startEdit = (row) => {
    setEditId(row.id);
    setForm({
      class_id: String(row.class_id || ""),
      fee_type_id: String(row.fee_type_id || ""),
      amount: String(row.amount || ""),
      frequency: row.frequency || "monthly",
    });
  };

  return (
    <div data-testid="fee-master-custom">
      <PageTitle
        title="Fee Master"
        subtitle="Create class-wise fee amount and link it to fee type frequency"
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 mb-4">
        <div>
          <Label className="text-xs mb-1 block">Class</Label>
          <select
            className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
            value={form.class_id}
            onChange={(e) => setForm({ ...form, class_id: e.target.value })}
          >
            <option value="">Select class</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name || c.class_name || `Class ${c.id}`}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs mb-1 block">Fee Type</Label>
          <select
            className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
            value={form.fee_type_id}
            onChange={(e) => {
              const nextId = e.target.value;
              const ft = feeTypes.find((x) => String(x.id) === String(nextId));
              setForm({
                ...form,
                fee_type_id: nextId,
                frequency: ft?.frequency || "monthly",
              });
            }}
          >
            <option value="">Select fee type</option>
            {feeTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.frequency || "monthly"})
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs mb-1 block">Amount</Label>
          <Input
            type="number"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            placeholder="1200"
          />
        </div>
        <div>
          <Label className="text-xs mb-1 block">Frequency</Label>
          <select
            className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white"
            value={form.frequency}
            onChange={(e) => setForm({ ...form, frequency: e.target.value })}
          >
            <option value="monthly">Monthly (12)</option>
            <option value="quarterly">Quarterly (4)</option>
            <option value="yearly">Yearly (1)</option>
          </select>
        </div>
      </div>

      <Button
        onClick={save}
        disabled={
          busy ||
          (!editId && !permissions.canAdd) ||
          (editId && !permissions.canEdit)
        }
        className="mb-4"
      >
        {busy ? "Saving..." : editId ? "Update Fee Master" : "Save Fee Master"}
      </Button>
      {editId && (
        <Button
          type="button"
          variant="outline"
          className="mb-4 ml-2"
          onClick={() => {
            setEditId(null);
            setForm({
              class_id: "",
              fee_type_id: "",
              amount: "",
              frequency: "monthly",
            });
          }}
        >
          Cancel Edit
        </Button>
      )}

      <DataTable
        columns={[
          { key: "name", label: "Head" },
          {
            key: "class_id",
            label: "Class",
            render: (r) =>
              classes.find((c) => String(c.id) === String(r.class_id))?.name ||
              r.class_id,
          },
          {
            key: "fee_type_id",
            label: "Fee Type",
            render: (r) =>
              feeTypes.find((t) => String(t.id) === String(r.fee_type_id))
                ?.name || r.fee_type_id,
          },
          { key: "frequency", label: "Frequency" },
          { key: "amount", label: "Amount" },
          {
            key: "actions",
            label: "Actions",
            render: (r) => (
              <div className="flex gap-2">
                {permissions.canEdit && (
                  <button
                    className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50"
                    onClick={() => startEdit(r)}
                    type="button"
                  >
                    Edit
                  </button>
                )}
                {permissions.canDelete && (
                  <button
                    className="text-xs px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50"
                    onClick={() => remove(r.id)}
                    type="button"
                  >
                    Delete
                  </button>
                )}
              </div>
            ),
          },
        ]}
        rows={rows}
      />
    </div>
  );
}

function ConcessionPage() {
  const permissions = useRouteModulePermissions("fees", "concession");
  const { branchId, sessionId, user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [allAssigns, setAllAssigns] = useState([]);
  const [assigns, setAssigns] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [concessionAmounts, setConcessionAmounts] = useState({});
  const [concessionDate, setConcessionDate] = useState(todayIsoDate());
  const [reason, setReason] = useState("");
  const [rows, setRows] = useState([]);
  const studentOptions = studentComboboxOptions(students);
  const canApproveConcession = ["admin", "superadmin"].includes(
    String(user?.role || "").toLowerCase(),
  );
  const selectedStudent = students.find(
    (student) => String(student.id) === String(studentId),
  );

  const classLabel = (classId) =>
    classes.find((c) => String(c.id) === String(classId))?.name ||
    classes.find((c) => String(c.id) === String(classId))?.class_name ||
    classId ||
    "-";

  const loadAssignsForStudent = async (nextStudentId = studentId) => {
    if (!nextStudentId) {
      setAssigns([]);
      return;
    }
    const { data } = await api.get("/fees/assign", {
      params: {
        branch_id: branchId,
        session_id: sessionId,
        student_id: nextStudentId,
      },
    });
    setAssigns((data.items || []).filter((r) => Number(r.due || 0) > 0));
  };

  const loadRows = async () => {
    const { data } = await api.get("/fees/concession");
    setRows(data.items || []);
  };

  useEffect(() => {
    (async () => {
      const [studentRes, classRes, assignRes] = await Promise.all([
        api.get("/students", {
          params: { branch_id: branchId, session_id: sessionId, limit: 1000 },
        }),
        api.get("/classes", { params: { branch_id: branchId, limit: 500 } }),
        api.get("/fees/assign", {
          params: { branch_id: branchId, session_id: sessionId },
        }),
      ]);
      setStudents(studentRes.data.items || []);
      setClasses(classRes.data.items || []);
      setAllAssigns(assignRes.data.items || []);
      loadRows();
    })();
  }, [branchId, sessionId]);

  useEffect(() => {
    if (!studentId) {
      setAssigns([]);
      setConcessionAmounts({});
      return;
    }
    loadAssignsForStudent(studentId);
  }, [studentId, branchId, sessionId]);

  const save = async () => {
    if (!permissions.canAdd) {
      toast.error("You do not have add permission for concession");
      return;
    }
    const items = assigns
      .map((assign) => ({
        fee_assign_id: assign.id,
        amount: Number(concessionAmounts[assign.id] || 0),
        reason,
      }))
      .filter((item) => item.amount > 0);
    if (items.length === 0) {
      return toast.error("Enter concession amount in at least one fee head");
    }
    await api.post("/fees/concession", {
      items,
      date: concessionDate,
      reason,
      status: "pending",
    });
    toast.success(
      `${items.length} concession entr${items.length > 1 ? "ies" : "y"} added for approval`,
    );
    setConcessionAmounts({});
    setReason("");
    await Promise.all([
      loadRows(),
      loadAssignsForStudent(studentId),
      (async () => {
        const { data } = await api.get("/fees/assign", {
          params: { branch_id: branchId, session_id: sessionId },
        });
        setAllAssigns(data.items || []);
      })(),
    ]);
  };

  const setLineAmount = (assignId, value, maxDue) => {
    const nextAmount = Math.max(
      0,
      Math.min(Number(value) || 0, Number(maxDue) || 0),
    );
    setConcessionAmounts((prev) => ({
      ...prev,
      [assignId]: nextAmount ? String(nextAmount) : "",
    }));
  };

  const enrichedRows = rows.map((row) => {
    const student = students.find(
      (item) => String(item.id) === String(row.student_id),
    );
    const assign = allAssigns.find(
      (item) => String(item.id) === String(row.fee_assign_id),
    );
    return {
      ...row,
      student_name: student
        ? `${student.first_name || ""} ${student.last_name || ""}`.trim()
        : "-",
      admission_no: student?.admission_no || "",
      class_label: classLabel(student?.class_id),
      section: student?.section || student?.sections?.[0] || "-",
      fee_head: assign?.narration || `Bill #${row.fee_assign_id}`,
    };
  });

  const approveConcession = async (row) => {
    if (!permissions.canEdit) {
      toast.error("You do not have approval permission for concession");
      return;
    }
    await api.post(`/fees/concession/${row.id}/approve`, {});
    toast.success("Concession approved");
    await Promise.all([loadRows(), loadAssignsForStudent(studentId)]);
  };

  return (
    <div data-testid="concession-page">
      <PageTitle
        title="Fee Concession"
        subtitle="Post concession against one or more bills. Student ledger will reduce automatically."
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <div>
          <Label className="text-xs mb-1 block">Student</Label>
          <Combobox
            value={String(studentId || "")}
            onChange={(value) => setStudentId(value)}
            options={studentOptions}
            placeholder="Search student"
            searchPlaceholder="Type student name / admission no"
            testid="concession-student"
          />
        </div>
        <div>
          <Label className="text-xs mb-1 block">Concession Date</Label>
          <Input
            type="date"
            value={concessionDate}
            onChange={(e) => setConcessionDate(e.target.value)}
          />
        </div>
        <div>
          <Label className="text-xs mb-1 block">Reason</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
      </div>
      {selectedStudent ? (
        <div className="border border-slate-200 bg-white rounded-md p-3 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div>
            <div className="text-xs font-semibold text-slate-500">
              Student Name
            </div>
            <div className="font-medium text-slate-900">
              {`${selectedStudent.first_name || ""} ${selectedStudent.last_name || ""}`.trim() ||
                "-"}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-500">
              Class / Section
            </div>
            <div className="font-medium text-slate-900">
              {classLabel(selectedStudent.class_id)} /{" "}
              {selectedStudent.section || "-"}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-500">
              Admission No
            </div>
            <div className="font-medium text-slate-900">
              {selectedStudent.admission_no || "-"}
            </div>
          </div>
        </div>
      ) : null}
      {studentId ? (
        <div className="border border-slate-200 bg-white rounded-md p-3 mb-4">
          <div className="text-sm font-semibold mb-3">Pending Fee Heads</div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-slate-600">
                  <th className="py-2 pr-3">Fee Head</th>
                  <th className="py-2 pr-3">Due Date</th>
                  <th className="py-2 pr-3">Open Due</th>
                  <th className="py-2 pr-3">Concession Amount</th>
                </tr>
              </thead>
              <tbody>
                {assigns.map((assign) => (
                  <tr key={assign.id} className="border-b border-slate-100">
                    <td className="py-2 pr-3 font-medium text-slate-900">
                      {assign.narration || "Fee Head"}
                    </td>
                    <td className="py-2 pr-3">
                      {formatDisplayDate(assign.due_date)}
                    </td>
                    <td className="py-2 pr-3 font-mono text-rose-700">
                      ₹{Number(assign.due || 0).toFixed(2)}
                    </td>
                    <td className="py-2 pr-3 w-44">
                      <Input
                        type="number"
                        min="0"
                        max={Number(assign.due || 0)}
                        value={concessionAmounts[assign.id] || ""}
                        onChange={(e) =>
                          setLineAmount(assign.id, e.target.value, assign.due)
                        }
                        placeholder="0.00"
                      />
                    </td>
                  </tr>
                ))}
                {assigns.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="py-4 text-slate-500">
                      No pending fee heads found for this student.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
      <Button onClick={save} disabled={!permissions.canAdd}>
        Post Concession
      </Button>

      <div className="mt-4">
        <DataTable
          columns={[
            {
              key: "student_name",
              label: "Student",
              render: (r) => (
                <div className="leading-tight">
                  <div className="font-medium">{r.student_name || "-"}</div>
                  <div className="text-[11px] text-slate-500">
                    {r.admission_no || ""}
                  </div>
                </div>
              ),
            },
            { key: "class_label", label: "Class" },
            { key: "section", label: "Section" },
            { key: "fee_head", label: "Fee Head" },
            {
              key: "created_at",
              label: "Date",
              render: (r) =>
                formatDisplayDate(String(r.created_at || "").slice(0, 10)),
            },
            { key: "amount", label: "Concession" },
            { key: "reason", label: "Reason" },
            { key: "status", label: "Status" },
            {
              key: "added_by_name",
              label: "Added By",
              render: (r) => r.added_by_name || "-",
            },
            {
              key: "approved_by_name",
              label: "Approved By",
              render: (r) => r.approved_by_name || "-",
            },
            {
              key: "actions",
              label: "Actions",
              render: (r) =>
                canApproveConcession &&
                String(r.status || "") !== "approved" &&
                permissions.canEdit ? (
                  <button
                    type="button"
                    onClick={() => approveConcession(r)}
                    className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 font-semibold"
                  >
                    Approve
                  </button>
                ) : (
                  <span className="text-xs text-slate-400">-</span>
                ),
            },
          ]}
          rows={enrichedRows}
        />
      </div>
    </div>
  );
}

function StudentLedgerPage() {
  const { branchId, sessionId } = useAuth();
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [data, setData] = useState(null);
  const studentOptions = studentComboboxOptions(students);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/students", {
        params: { branch_id: branchId, session_id: sessionId, limit: 1000 },
      });
      setStudents(data.items || []);
    })();
  }, [branchId, sessionId]);

  // Default To Date to the active session's own end date (once) — a sensible
  // upper bound so the view isn't open-ended. From Date is deliberately left
  // blank by default: the ledger is cumulative across every session (a
  // student's admission-time dues like Uniform/Book/Registration/Admission
  // fee are often dated before the current session even started), so
  // defaulting it to the session start date would silently hide those older
  // bills and receipts — exactly the "receipt exists but isn't showing"
  // symptom this guards against. "Clear Dates" continues to mean "show the
  // full cumulative ledger", so this only fires the first time it resolves.
  const defaultedDatesRef = useRef(false);
  useEffect(() => {
    if (!sessionId || defaultedDatesRef.current) return;
    (async () => {
      try {
        const { data } = await api.get("/sessions", {
          params: { branch_id: branchId, limit: 100 },
        });
        const row = (data.items || []).find(
          (s) => String(s.id) === String(sessionId),
        );
        const end = (row?.end_date || "").slice(0, 10);
        if (end) {
          defaultedDatesRef.current = true;
          setToDate((prev) => prev || end);
        }
      } catch {
        // Dates just stay blank (full ledger) if this fails.
      }
    })();
  }, [branchId, sessionId]);

  useEffect(() => {
    if (!studentId) {
      setData(null);
      return;
    }
    (async () => {
      const { data } = await api.get(`/fees/student-ledger/${studentId}`, {
        params: {
          branch_id: branchId,
          session_id: sessionId,
          from_date: fromDate || undefined,
          to_date: toDate || undefined,
        },
      });
      setData(data);
    })();
  }, [studentId, branchId, sessionId, fromDate, toDate]);

  return (
    <div data-testid="student-ledger-page">
      <PageTitle
        title="Student Ledger"
        subtitle="Tally-style bill-wise student receivable ledger"
        actions={
          studentId
            ? [
                <Button
                  key="print"
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const params = new URLSearchParams();
                    params.append("format", "html");
                    params.append("autoprint", "1");
                    if (fromDate) params.append("from_date", fromDate);
                    if (toDate) params.append("to_date", toDate);
                    openAuthedReport(
                      `/api/fees/student-ledger/${studentId}/pdf?${params.toString()}`,
                    );
                  }}
                >
                  Print A4
                </Button>,
                <Button
                  key="pdf"
                  type="button"
                  onClick={async () => {
                    try {
                      const student = students.find(
                        (s) => String(s.id) === String(studentId),
                      );
                      const studentName = student
                        ? `${student.first_name || ""} ${student.last_name || ""}`.trim()
                        : "student";
                      const params = new URLSearchParams();
                      if (fromDate) params.append("from_date", fromDate);
                      if (toDate) params.append("to_date", toDate);
                      const queryString = params.toString();
                      await downloadAuthedReport(
                        `/api/fees/student-ledger/${studentId}/pdf${queryString ? "?" + queryString : ""}`,
                        `student-ledger-${studentName || studentId}.pdf`,
                      );
                    } catch (error) {
                      toast.error(error.message || "Download failed");
                    }
                  }}
                >
                  Download PDF
                </Button>,
              ]
            : null
        }
      />
      <div className="border border-slate-200 bg-white rounded-md p-3 mb-3 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1 block">
            Student
          </Label>
          <Combobox
            value={String(studentId || "")}
            onChange={(value) => setStudentId(value)}
            options={studentOptions}
            placeholder="Search student"
            searchPlaceholder="Type student name / admission no"
            testid="student-ledger-student"
          />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1 block">
            From Date
          </Label>
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="h-9 text-sm"
          />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1 block">
            To Date
          </Label>
          <Input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="h-9 text-sm"
          />
        </div>
        <div className="flex items-end">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => {
              setFromDate("");
              setToDate("");
            }}
          >
            Clear Dates
          </Button>
        </div>
      </div>

      {data && (
        <div className="space-y-4">
          <div className="border border-slate-200 bg-white rounded-md p-3 text-sm">
            <div className="font-semibold">
              {data.student?.first_name} {data.student?.last_name}
            </div>
            <div className="text-slate-500 text-xs">{data.ledger?.name}</div>
            <div className="mt-2 font-mono">
              Balance ₹{Number(data.ledger?.balance || 0).toFixed(2)}
            </div>
          </div>

          <div className="border border-slate-200 bg-white rounded-md overflow-hidden">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-slate-100 text-xs uppercase text-slate-700">
                <tr>
                  <th className="px-2 py-1.5 text-left border-b border-slate-300">
                    Date
                  </th>
                  <th className="px-2 py-1.5 text-left border-b border-slate-300">
                    Voucher
                  </th>
                  <th className="px-2 py-1.5 text-left border-b border-slate-300">
                    Narration
                  </th>
                  <th className="px-2 py-1.5 text-right border-b border-slate-300">
                    Dr
                  </th>
                  <th className="px-2 py-1.5 text-right border-b border-slate-300">
                    Cr
                  </th>
                  <th className="px-2 py-1.5 text-right border-b border-slate-300">
                    Balance
                  </th>
                </tr>
              </thead>
              <tbody>
                {(data.ledger?.rows || []).map((r, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="px-2 py-1.5">{formatDisplayDate(r.date)}</td>
                    <td className="px-2 py-1.5">{r.voucher_no}</td>
                    <td className="px-2 py-1.5">{r.narration}</td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {r.dr ? `₹${Number(r.dr).toFixed(2)}` : ""}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {r.cr ? `₹${Number(r.cr).toFixed(2)}` : ""}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      ₹{Number(r.balance || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <h3 className="text-sm font-semibold mb-2">Open Bills</h3>
            <DataTable
              columns={[
                { key: "narration", label: "Fee Head" },
                { key: "amount", label: "Amount" },
                { key: "paid", label: "Paid" },
                { key: "concession", label: "Concession" },
                { key: "due", label: "Due" },
                {
                  key: "due_date",
                  label: "Due Date",
                  render: (r) => formatDisplayDate(r.due_date),
                },
              ]}
              rows={data.bills || []}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function Outstanding() {
  const { branchId, sessionId } = useAuth();
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [filters, setFilters] = useState({
    classId: "",
    section: "",
    studentId: "",
  });
  const [data, setData] = useState({
    rows: [],
    total_students: 0,
    total_amount: 0,
    total_paid: 0,
    total_due: 0,
  });
  const [metaLoading, setMetaLoading] = useState(true);

  const load = useCallback(async () => {
    const { data } = await api.get("/fees/outstanding", {
      params: {
        branch_id: branchId,
        session_id: sessionId,
        class_id: filters.classId || undefined,
        section: filters.section || undefined,
        student_id: filters.studentId || undefined,
      },
    });
    setData({
      rows: data.rows || [],
      total_students: Number(data.total_students || 0),
      total_amount: Number(data.total_amount || 0),
      total_paid: Number(data.total_paid || 0),
      total_due: Number(data.total_due || 0),
    });
  }, [
    branchId,
    filters.classId,
    filters.section,
    filters.studentId,
    sessionId,
  ]);

  const loadMeta = useCallback(async () => {
    setMetaLoading(true);
    try {
      const [classRes, studentRes] = await Promise.all([
        api.get("/classes", { params: { branch_id: branchId, limit: 500 } }),
        api.get("/students", {
          params: {
            branch_id: branchId,
            session_id: sessionId,
            class_id: filters.classId || undefined,
            section: filters.section || undefined,
            limit: 1000,
          },
        }),
      ]);
      setClasses(classRes.data.items || []);
      setStudents(studentRes.data.items || []);
    } finally {
      setMetaLoading(false);
    }
  }, [branchId, filters.classId, filters.section, sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, studentId: "" }));
  }, [filters.classId, filters.section]);

  const selectedClass = classes.find(
    (item) => String(item.id) === String(filters.classId),
  );
  const sectionOptions = selectedClass?.sections || [];
  const studentOptions = studentComboboxOptions(students);

  const exportRows = () => {
    if ((data.rows || []).length === 0) {
      toast.info("Nothing to export");
      return;
    }
    downloadCSV(
      [
        ...data.rows,
        {
          student_name: "TOTAL",
          admission_no: "",
          class_label: "",
          section: "",
          total_amount: Number(data.total_amount || 0).toFixed(2),
          total_paid: Number(data.total_paid || 0).toFixed(2),
          total_due: Number(data.total_due || 0).toFixed(2),
        },
      ],
      [
        { key: "student_name", label: "Student" },
        { key: "admission_no", label: "Adm No" },
        { key: "class_label", label: "Class" },
        { key: "section", label: "Section" },
        {
          key: "total_amount",
          label: "Total Fees",
          csv: (r) => Number(r.total_amount || 0).toFixed(2),
        },
        {
          key: "total_paid",
          label: "Total Paid",
          csv: (r) => Number(r.total_paid || 0).toFixed(2),
        },
        {
          key: "total_due",
          label: "Total Due",
          csv: (r) => Number(r.total_due || 0).toFixed(2),
        },
      ],
      "outstanding_student_wise.csv",
    );
    toast.success("Exported");
  };

  return (
    <div data-testid="outstanding">
      <PageTitle
        title="Outstanding Fees"
        subtitle={`${data.total_students || 0} students • Total fees: ₹${Number(data.total_amount || 0).toLocaleString()} • Paid: ₹${Number(data.total_paid || 0).toLocaleString()} • Due: ₹${Number(data.total_due || 0).toLocaleString()}`}
        actions={[
          <Button
            key="export"
            type="button"
            variant="outline"
            onClick={exportRows}
          >
            Excel Export
          </Button>,
        ]}
      />

      <div className="border border-slate-200 bg-white rounded-md p-3 mb-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1 block">
            Class
          </Label>
          <select
            className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white disabled:opacity-60 disabled:cursor-wait"
            value={filters.classId}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                classId: e.target.value,
                section: "",
                studentId: "",
              }))
            }
            disabled={metaLoading}
          >
            <option value="">{metaLoading ? "Loading classes…" : "All Classes"}</option>
            {!metaLoading &&
              classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || c.class_name || `Class ${c.id}`}
                </option>
              ))}
          </select>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1 block">
            Section
          </Label>
          <select
            className="h-9 w-full rounded-md border border-slate-300 text-sm px-2 bg-white disabled:opacity-60 disabled:cursor-wait"
            value={filters.section}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                section: e.target.value,
                studentId: "",
              }))
            }
            disabled={metaLoading}
          >
            <option value="">{metaLoading ? "Loading…" : "All Sections"}</option>
            {!metaLoading &&
              sectionOptions.map((section) => (
                <option key={section} value={section}>
                  {section}
                </option>
              ))}
          </select>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1 block">
            Student
          </Label>
          <Combobox
            value={String(filters.studentId || "")}
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, studentId: value }))
            }
            options={studentOptions}
            placeholder="All students"
            searchPlaceholder="Search student"
            loading={metaLoading}
            testid="outstanding-filter-student"
          />
        </div>
        <div className="flex items-end">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() =>
              setFilters({
                classId: "",
                section: "",
                studentId: "",
              })
            }
          >
            Reset Filters
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-700 uppercase text-xs tracking-wide">
              <tr>
                <th className="px-4 py-3 text-left">Student</th>
                <th className="px-4 py-3 text-left">Adm No</th>
                <th className="px-4 py-3 text-left">Class</th>
                <th className="px-4 py-3 text-left">Section</th>
                <th className="px-4 py-3 text-right">Total Fees</th>
                <th className="px-4 py-3 text-right">Total Paid</th>
                <th className="px-4 py-3 text-right">Total Due</th>
              </tr>
            </thead>
            <tbody>
              {(data.rows || []).length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-8 text-center text-slate-500"
                  >
                    No outstanding students found.
                  </td>
                </tr>
              ) : (
                data.rows.map((row) => (
                  <tr
                    key={row.student_id}
                    className="border-t border-slate-200"
                  >
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {row.student_name || "-"}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {row.admission_no || "-"}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {row.class_label || row.class_id || "-"}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {row.section || "-"}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-700">
                      ₹{Number(row.total_amount || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-700">
                      ₹{Number(row.total_paid || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-red-600">
                      ₹{Number(row.total_due || 0).toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot className="bg-amber-50 border-t border-slate-200">
              <tr>
                <td className="px-4 py-3 font-semibold text-slate-900">
                  Total
                </td>
                <td className="px-4 py-3 text-slate-500">
                  {data.total_students || 0} students
                </td>
                <td className="px-4 py-3" colSpan={2}></td>
                <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">
                  ₹{Number(data.total_amount || 0).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">
                  ₹{Number(data.total_paid || 0).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-right font-mono font-bold text-red-600">
                  ₹{Number(data.total_due || 0).toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

function LedgerView() {
  const { branchId, sessionId } = useAuth();
  const [ledgers, setLedgers] = useState([]);
  const [sel, setSel] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/ledgers");
      setLedgers(data.items || []);
    })();
  }, []);

  useEffect(() => {
    if (!sel) {
      setData(null);
      return;
    }
    (async () => {
      const { data } = await api.get(`/fees/ledger/${sel}`, {
        params: { branch_id: branchId, session_id: sessionId },
      });
      setData(data);
    })();
  }, [sel, branchId, sessionId]);

  return (
    <div data-testid="ledger-view">
      <PageTitle title="Ledger Report" subtitle="Tally-style Dr / Cr view" />
      <div className="mb-3">
        <select
          value={sel}
          onChange={(e) => setSel(e.target.value)}
          className="h-9 w-72 rounded-md border border-slate-300 text-sm px-2 bg-white"
          data-testid="ledger-select"
        >
          <option value="">— Select ledger —</option>
          {ledgers.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
      </div>
      {data && (
        <div className="border border-slate-200 bg-white rounded-md overflow-hidden">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-slate-100 text-xs uppercase text-slate-700">
              <tr>
                <th className="px-2 py-1.5 text-left border-b border-slate-300">
                  Date
                </th>
                <th className="px-2 py-1.5 text-left border-b border-slate-300">
                  Voucher
                </th>
                <th className="px-2 py-1.5 text-left border-b border-slate-300">
                  Narration
                </th>
                <th className="px-2 py-1.5 text-right border-b border-slate-300">
                  Debit
                </th>
                <th className="px-2 py-1.5 text-right border-b border-slate-300">
                  Credit
                </th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="px-2 py-1.5 font-mono">
                    {formatDisplayDate(r.date)}
                  </td>
                  <td className="px-2 py-1.5">{r.voucher_no}</td>
                  <td className="px-2 py-1.5 text-slate-600">{r.narration}</td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {r.dr ? `₹${r.dr.toFixed(2)}` : ""}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {r.cr ? `₹${r.cr.toFixed(2)}` : ""}
                  </td>
                </tr>
              ))}
              <tr className="bg-slate-50 font-semibold border-t-2 border-slate-300">
                <td colSpan={3} className="px-2 py-1.5 text-right">
                  Total
                </td>
                <td className="px-2 py-1.5 text-right font-mono">
                  ₹{data.total_dr.toFixed(2)}
                </td>
                <td className="px-2 py-1.5 text-right font-mono">
                  ₹{data.total_cr.toFixed(2)}
                </td>
              </tr>
              <tr className="bg-blue-50 font-bold border-t border-slate-300">
                <td colSpan={3} className="px-2 py-1.5 text-right">
                  Balance
                </td>
                <td colSpan={2} className="px-2 py-1.5 text-right font-mono">
                  ₹{(data.total_dr - data.total_cr).toFixed(2)}{" "}
                  {data.total_dr - data.total_cr >= 0 ? "Dr" : "Cr"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Vouchers() {
  const { branchId, sessionId } = useAuth();
  const [rows, setRows] = useState([]);
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/fees/vouchers", {
        params: { branch_id: branchId, session_id: sessionId },
      });
      setRows(data.items || []);
    })();
  }, [branchId, sessionId]);
  return (
    <div data-testid="vouchers">
      <PageTitle title="Vouchers" subtitle="Tally-style journal vouchers" />
      <DataTable
        columns={[
          { key: "voucher_no", label: "Voucher #" },
          { key: "voucher_type", label: "Type" },
          {
            key: "date",
            label: "Date",
            render: (r) => formatDisplayDate(r.date),
          },
          {
            key: "total_dr",
            label: "Dr",
            render: (r) => `₹${Number(r.total_dr).toFixed(2)}`,
          },
          {
            key: "total_cr",
            label: "Cr",
            render: (r) => `₹${Number(r.total_cr).toFixed(2)}`,
          },
          { key: "receipt_no", label: "Receipt" },
        ]}
        rows={rows}
      />
    </div>
  );
}

export default function Fees() {
  const receiptPermissions = useRouteModulePermissions("fees", "receipt");
  const receiptsPermissions = useRouteModulePermissions("fees", "receipts");
  const reconciliationPermissions = useRouteModulePermissions(
    "fees",
    "reconciliation",
  );
  const concessionPermissions = useRouteModulePermissions("fees", "concession");
  const assignPermissions = useRouteModulePermissions("fees", "assign");
  const miscAssignPermissions = useRouteModulePermissions(
    "fees",
    "misc_assign",
  );
  const outstandingPermissions = useRouteModulePermissions(
    "fees",
    "outstanding",
  );
  const studentLedgerPermissions = useRouteModulePermissions(
    "fees",
    "student_ledger",
  );
  const ledgerPermissions = useRouteModulePermissions("fees", "ledger");
  const vouchersPermissions = useRouteModulePermissions("fees", "vouchers");
  const feeMasterPermissions = useRouteModulePermissions("fees", "fee_master");
  const feeTypesPermissions = useRouteModulePermissions("fees", "fee_types");
  const ledgerMasterPermissions = useRouteModulePermissions(
    "fees",
    "ledger_master",
  );
  const items = [
    receiptPermissions.canView && { to: "/fees/receipt", label: "Collect Fee" },
    receiptsPermissions.canView && { to: "/fees/receipts", label: "Receipts" },
    reconciliationPermissions.canView && {
      to: "/fees/reconciliation",
      label: "Cheque Reconciliation",
    },
    concessionPermissions.canView && {
      to: "/fees/concession",
      label: "Concession",
    },
    outstandingPermissions.canView && {
      to: "/fees/outstanding",
      label: "Outstanding",
    },
    studentLedgerPermissions.canView && {
      to: "/fees/student-ledger",
      label: "Student Ledger",
    },
    ledgerPermissions.canView && { to: "/fees/ledger", label: "Ledgers" },
    vouchersPermissions.canView && { to: "/fees/vouchers", label: "Vouchers" },
    feeTypesPermissions.canView && {
      to: "/fees/fee-types",
      label: "Fee Types",
    },
    feeMasterPermissions.canView && {
      to: "/fees/fee-master",
      label: "Fee Master",
    },
    assignPermissions.canView && { to: "/fees/assign", label: "Fee Assign" },
    miscAssignPermissions.canView && {
      to: "/fees/misc-assign",
      label: "Miscellaneous Fees",
    },
    ledgerMasterPermissions.canView && {
      to: "/fees/ledger-master",
      label: "Ledger Master",
    },
  ].filter(Boolean);
  if (items.length === 0) {
    return (
      <div>
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center">
          <div className="text-base font-semibold text-slate-700">
            No fees workflow assigned
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Ask super admin to enable Fees sub-features for this role.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div>
      <FeesNav items={items} />
      <Routes>
        <Route index element={<Navigate to={items[0].to} replace />} />
        {receiptPermissions.canView && (
          <Route path="receipt" element={<CollectFee />} />
        )}
        {receiptsPermissions.canView && (
          <Route path="receipts" element={<ReceiptsList />} />
        )}
        {reconciliationPermissions.canView && (
          <Route path="reconciliation" element={<ChequeReconciliationPage />} />
        )}
        {concessionPermissions.canView && (
          <Route path="concession" element={<ConcessionPage />} />
        )}
        {assignPermissions.canView && (
          <Route path="assign" element={<AssignFee />} />
        )}
        {miscAssignPermissions.canView && (
          <Route path="misc-assign" element={<MiscFeeAssign />} />
        )}
        {outstandingPermissions.canView && (
          <Route path="outstanding" element={<Outstanding />} />
        )}
        {studentLedgerPermissions.canView && (
          <Route path="student-ledger" element={<StudentLedgerPage />} />
        )}
        {ledgerPermissions.canView && (
          <Route path="ledger" element={<LedgerView />} />
        )}
        {vouchersPermissions.canView && (
          <Route path="vouchers" element={<Vouchers />} />
        )}
        {feeMasterPermissions.canView && (
          <Route path="fee-master" element={<FeeMasterPage />} />
        )}
        {feeTypesPermissions.canView && (
          <Route path="fee-types" element={<FeeTypesPage />} />
        )}
        {ledgerMasterPermissions.canView && (
          <Route
            path="ledger-master"
            element={
              <MasterCrudPage
                title="Ledger Master"
                endpoint="/ledgers"
                testid="ledger-master"
                moduleKey="fees"
                submoduleKey="ledger_master"
                fields={[
                  { key: "name", label: "Name", required: true },
                  {
                    key: "type",
                    label: "Type",
                    type: "select",
                    options: [
                      { value: "asset", label: "Asset" },
                      { value: "liability", label: "Liability" },
                      { value: "income", label: "Income" },
                      { value: "expense", label: "Expense" },
                    ],
                  },
                  { key: "group", label: "Group" },
                  {
                    key: "opening_balance",
                    label: "Opening Balance",
                    type: "number",
                  },
                ]}
                columns={[
                  { key: "name", label: "Name" },
                  { key: "type", label: "Type" },
                  { key: "group", label: "Group" },
                  { key: "opening_balance", label: "Opening" },
                ]}
              />
            }
          />
        )}
      </Routes>
    </div>
  );
}
